// Phase 36 Batch V — PWA install prompt UX.
//
// Two surfaces:
//   1. Non-iOS (Chrome, Edge, Samsung): capture the `beforeinstallprompt`
//      event and show a small floating CTA. Tapping it triggers the
//      browser's native install dialog.
//   2. iOS Safari (which never fires `beforeinstallprompt`): show a
//      one-time dismissible hint explaining the manual Share → Add to
//      Home Screen path.
//
// Behaviour rules:
//   * Never show in standalone mode (the user already installed it).
//   * Once a user dismisses or accepts the prompt, don't show it again
//     for 30 days. Persisted in localStorage.
//   * Render unobtrusively (small chip, bottom-right). Never block UI.
//   * No-op on SSR / non-browser contexts.

import { useEffect, useState } from "react";
import { Download, X, Share2 } from "lucide-react";

// Batch AV — both localStorage touches in this file are categorised
// as PREFERENCE. `DISMISS_KEY` stores a single number: the epoch ms
// when the user last dismissed the PWA install hint. It carries no
// PII and no auth state; reading it via XSS yields only the
// dismissal timestamp. Keeping it in localStorage is the right
// store because (a) it must survive page reloads to enforce the
// 30-day cooldown and (b) it's irrelevant to other devices the
// same user logs in from.
const DISMISS_KEY = "runidea.pwa.installPromptDismissedAt";
const REMIND_AFTER_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function _isStandalone() {
  if (typeof window === "undefined") return false;
  // Chrome / Android
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
}

function _isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return true;
  // iPadOS 13+ reports as MacIntel but supports touch.
  if (ua.includes("Macintosh") && navigator.maxTouchPoints > 1) return true;
  return false;
}

function _recentlyDismissed() {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < REMIND_AFTER_MS;
  } catch {
    return false;
  }
}

function _markDismissed() {
  try { window.localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
}

export default function InstallPrompt() {
  const [variant, setVariant] = useState(null); // 'native' | 'ios' | null
  const [installEvent, setInstallEvent] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (_isStandalone()) return;       // already installed
    if (_recentlyDismissed()) return;  // user said no recently

    // Native install prompt path — Chrome / Edge / Android.
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      setVariant("native");
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari path — never fires beforeinstallprompt. Delay the hint
    // a couple seconds so it doesn't pile onto the initial-load LCP.
    let iosTimer = null;
    if (_isIOS()) {
      iosTimer = window.setTimeout(() => setVariant("ios"), 3000);
    }

    // If they install via Chrome's address-bar UI we get this event too.
    const onInstalled = () => { setVariant(null); _markDismissed(); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, []);

  if (!variant) return null;

  const handleNativeInstall = async () => {
    if (!installEvent) return;
    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      // Either way (accepted or dismissed) — don't pester again soon.
      _markDismissed();
      if (choice && choice.outcome === "accepted") {
        setVariant(null);
      } else {
        setVariant(null);
      }
    } catch {
      setVariant(null);
    }
  };

  const dismiss = () => { _markDismissed(); setVariant(null); };

  // ── shared chrome ─────────────────────────────────────────────────
  const wrap = "fixed z-50 bottom-4 right-4 max-w-xs rounded-2xl border border-slate-200 bg-white p-4 shadow-lg";

  if (variant === "native") {
    return (
      <div
        data-testid="pwa-install-prompt-native"
        className={wrap}
        role="dialog"
        aria-label="Install RunIdea"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <Download className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900">Install RunIdea</p>
            <p className="mt-0.5 text-xs text-slate-600">
              Add to your home screen for one-tap access.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleNativeInstall}
                data-testid="pwa-install-accept"
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                Install
              </button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                className="text-xs font-medium text-slate-500 hover:text-slate-700"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="flex-none text-slate-300 hover:text-slate-500"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // iOS variant
  return (
    <div
      data-testid="pwa-install-prompt-ios"
      className={wrap}
      role="dialog"
      aria-label="Install RunIdea on iOS"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
          <Share2 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Add to Home Screen</p>
          <p className="mt-0.5 text-xs text-slate-600">
            Tap <span className="font-medium">Share</span> in Safari, then
            <span className="font-medium"> Add to Home Screen</span> to open
            RunIdea like an app.
          </p>
          <button
            type="button"
            onClick={dismiss}
            data-testid="pwa-install-dismiss"
            className="mt-2 text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            Got it
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex-none text-slate-300 hover:text-slate-500"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
